import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  BoxProps,
  Dialog,
  dialogClasses,
  DialogProps,
  IconButton,
  Theme,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { useAllMQ } from '~/base/hooks/useAllMQ';

type EnhancedReason = 'closeButton' | 'backdropClick' | 'escapeKeyDown';

export type QModalProps = Omit<DialogProps, 'onClose'> & {
  title?: string;
  titleProps?: TypographyProps;
  contentProps?: BoxProps;
  hideHeader?: boolean;
  customMaxWidth?: number;
  onClose?: (reason?: EnhancedReason) => void;
};

/**
 * Кастомная модалка с заголовком и кнопкой закрыть. Использует компонент Dialog из MUI.
 *
 * @param title заголовок
 * @param titleProps пропсы для заголовка
 * @param contentProps пропсы для обертки children внутри Dialog
 * @param hideHeader скрыть шапку
 * @param customMaxWidth максимальная ширина модалки
 */
export const QModal: React.FC<QModalProps> = (props) => {
  const {
    title,
    titleProps,
    contentProps,
    hideHeader,
    customMaxWidth = 600,
    children,
    classes: dialogClasses,
    onClose,
    ...rest
  } = props;

  const theme = useTheme();
  const { isSM } = useAllMQ();
  const { classes, cx } = useStyles({ theme, customMaxWidth });

  // Handlers

  const handleClose = () => {
    onClose && onClose('closeButton');
  };

  // Renders

  return (
    <Dialog
      classes={{ ...dialogClasses, paper: cx(classes.paper, dialogClasses?.paper) }}
      onClose={() => handleClose()}
      scroll="body"
      {...rest}
    >
      {!hideHeader && (
        <div className={classes.header}>
          <Typography component="h2" flex={1} variant={isSM ? 'body1' : 'h6'} fontWeight={500} {...titleProps}>
            {title}
          </Typography>

          <IconButton size="small" onClick={handleClose}>
            <CloseIcon width={isSM ? 20 : 24} height={isSM ? 20 : 24} fill={theme.palette.custom.icon.secondary} />
          </IconButton>
        </div>
      )}

      <Box {...contentProps} className={cx(classes.content, contentProps?.className)}>
        {children}
      </Box>
    </Dialog>
  );
};

const useStyles = makeStyles<{ theme: Theme; customMaxWidth: number }>()((_, { theme, customMaxWidth }) => ({
  paper: {
    width: '100%',
    maxWidth: customMaxWidth || '',
    marginBottom: theme.spacing(4),
    borderRadius: theme.spacing(1),
    [`&.${dialogClasses.paperScrollBody}`]: {
      [theme.breakpoints.down('sm')]: {
        maxWidth: `calc(100% - ${theme.spacing(4)})`,
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
    },
  },
  header: {
    display: 'flex',
    padding: theme.spacing(3, 2.5, 3, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  content: {
    padding: theme.spacing(0, 3, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2, 2),
    },
  },
}));
